import * as Adaptive from "adaptivecards";
import { BooleanPropertyEditor, PropertySheet, PropertySheetCategory, StringPropertyEditor, TypedCardElementPeer } from "adaptivecards-designer";
import { Comment } from '../../shared/interfaces/ticket.interface';
import { handleValidationElement, removeOtherPropertyConfigurations } from "./Utils/CustomElementUtils";

const displayLabelProperty = new Adaptive.StringProperty(Adaptive.Versions.v1_0, "displayLabel", true);
const privateFieldProperty = new Adaptive.BoolProperty(Adaptive.Versions.v1_0, "privateField", false);
const showPrivacyProperty = new Adaptive.BoolProperty(Adaptive.Versions.v1_0, "showPrivacy", false);
const visibleFieldProperty = new Adaptive.BoolProperty(Adaptive.Versions.v1_0, "visibleField");
const contentFieldProperty = new Adaptive.StringProperty(Adaptive.Versions.v1_0, "contentField", false);
const requireFieldProperty = new Adaptive.BoolProperty(Adaptive.Versions.v1_0, "requireField", false);

export class AddComment extends Adaptive.TextInput {
    static readonly JsonTypeName = "Add Comment";
    static readonly ElementId = "Ticket.AddComment";
    static readonly defaultLabel = "Comment";
    static readonly displayLabelProperty = displayLabelProperty;
    static readonly privateFieldProperty = privateFieldProperty;
    static readonly showPrivacyProperty = showPrivacyProperty;
    static readonly visibleFieldProperty = visibleFieldProperty;
    static readonly contentFieldProperty = contentFieldProperty;
    static readonly requireFieldProperty = requireFieldProperty;
    static isEndUserPage: boolean; 

    //#region Schema

    @Adaptive.property(displayLabelProperty)
    get displayLabel(): string | undefined {
        return this.getValue(AddComment.displayLabelProperty);
    }

    set displayLabel(value: string | undefined) {
        this.setValue(AddComment.displayLabelProperty, value);
        this.updateLayout();
    }

    @Adaptive.property(requireFieldProperty)
    get requireField(): boolean | undefined {
        return this.getValue(AddComment.requireFieldProperty);
    }

    set requireField(value: boolean | undefined) {
        this.setValue(AddComment.requireFieldProperty, value);
        this.updateLayout();
    }

    @Adaptive.property(privateFieldProperty)
    get privateField(): boolean | undefined {
        return this.getValue(AddComment.privateFieldProperty);
    }

    set privateField(value: boolean | undefined) {
        this.setValue(AddComment.privateFieldProperty, value);
        this.updateLayout();
    }


    @Adaptive.property(showPrivacyProperty)
    get showPrivacy(): boolean | undefined {
        return this.getValue(AddComment.showPrivacyProperty);
    }

    set showPrivacy(value: boolean | undefined) {
        this.setValue(AddComment.showPrivacyProperty, value);
        this.updateLayout();
    }

    @Adaptive.property(visibleFieldProperty)
    get visibleField(): boolean | undefined {
        return this.getValue(AddComment.visibleFieldProperty);
    }

    set visibleField(value: boolean | undefined) {
        this.setValue(AddComment.visibleFieldProperty, value);
        this.updateLayout();
    }

    @Adaptive.property(contentFieldProperty)
    get contentField(): string | undefined {
        return this.getValue(AddComment.contentFieldProperty);
    }

    set contentField(value: string | undefined) {
        this.setValue(AddComment.contentFieldProperty, value);
        this.updateLayout();
    }
    
    //#endregion

    protected internalRender(): HTMLElement | undefined {
        this.id = AddComment.ElementId;
        if((this?.displayLabel ?? "").length == 0)
            this.displayLabel = AddComment.defaultLabel;
        if(this?.showPrivacy == undefined)
            this.showPrivacy = false;
        this.visibleField ??= true;
        this.contentField ??= "{{message}}";

        let element = document.createElement("div"); 
        element.setAttribute("style", "width: 100%");

        let textInput = new Adaptive.TextInput();
        textInput.setParent(this);
        textInput.label = this.displayLabel;
        textInput.isRequired = this.requireField || this.isRequired;
        textInput.isMultiline = true;
        textInput.onValueChanged = (sender) => {
            const addCommentValue = this.getValue(AddComment.valueProperty) || "{}";
            let commentObj = JSON.parse(addCommentValue) as Comment;
            if(commentObj){
                commentObj.Body = sender.value ?? "";
                this.setValue(AddComment.valueProperty, JSON.stringify(commentObj));
            }
            handleValidationElement(element);
        }

        let defaultCommentObj = (this.defaultValue) ? JSON.parse(this.defaultValue) as Comment : null;
        if(defaultCommentObj)
            textInput.defaultValue = defaultCommentObj.Body;

        const renderedElement =  textInput.render();

        let privateInput = new Adaptive.ToggleInput();
        privateInput.setParent(this);
        privateInput.title = "Internal Note";
        privateInput.isVisible = this.showPrivacy;
        privateInput.onValueChanged = (sender) => {
            const addCommentValue = this.getValue(AddComment.valueProperty) || "{}";
            let commentObj = JSON.parse(addCommentValue) as Comment;
            if(commentObj && sender.value){
                commentObj.IsPublic = sender.value !== 'true';
                this.setValue(AddComment.valueProperty, JSON.stringify(commentObj));
            }
            
        }

        if(defaultCommentObj)
            privateInput.defaultValue = !defaultCommentObj.hasOwnProperty("IsPublic") || defaultCommentObj.IsPublic ? "false" : "true";

        let privateElement = document.createElement("div"); 
        privateElement.setAttribute("style", "margin-top: 5px");

        privateElement.append(privateInput.render());

        if(this.showPrivacy && !AddComment.isEndUserPage)
            element.append(renderedElement, privateElement);
        else
            element.append(renderedElement);
        return element;
    }

    getJsonTypeName(): string {
        return AddComment.JsonTypeName;
    }

    get value(): string {
        return this.getValue(AddComment.valueProperty);
    }

    updateLayout(processChildren: boolean = true) {
        super.updateLayout(processChildren);
    }
}

export class AddCommentPeer extends TypedCardElementPeer<AddComment> {
    static readonly displayLabelProperty = new StringPropertyEditor(Adaptive.Versions.v1_0, "displayLabel", "Label", false);
    static readonly showPrivacyProperty = new BooleanPropertyEditor(Adaptive.Versions.v1_0, "showPrivacy", "Internal Note", false);
    static readonly visibleFieldProperty = new BooleanPropertyEditor(Adaptive.Versions.v1_0, "visibleField", "Initially visible", false);
    static readonly contentFieldProperty = new StringPropertyEditor(Adaptive.Versions.v1_0, "contentField", "Comment", false, true);
    static readonly requireFieldProperty = new BooleanPropertyEditor(Adaptive.Versions.v1_0, "requireField", "Required", false);
    
    populatePropertySheet(propertySheet: PropertySheet, defaultCategory: string = PropertySheetCategory.DefaultCategory) {
        super.populatePropertySheet(propertySheet, defaultCategory);
        removeOtherPropertyConfigurations(propertySheet);

        propertySheet.add(
            defaultCategory,
            AddCommentPeer.idProperty,
            AddCommentPeer.displayLabelProperty,
            AddCommentPeer.requireFieldProperty,
            AddCommentPeer.showPrivacyProperty,
            AddCommentPeer.visibleFieldProperty,
            AddCommentPeer.contentFieldProperty
        );
    }
}